import { isAxiosError, type AxiosInstance } from "axios";
import { useEffect, useSyncExternalStore } from "react";
import { dayjs } from "@/core/utils/date";
import {
  AI_SPREADSHEET_OPERATION_STATUS,
  type AiSpreadsheetOperationStatus,
  AcceptedAiSpreadsheetOperationResponse,
  AiSpreadsheetOperation,
} from "./types";

export type {
  AcceptedAiSpreadsheetOperationResponse,
  AiSpreadsheetOperation,
  AiSpreadsheetOperationResult,
  AiSpreadsheetOperationStatus,
} from "./types";

interface PersistedOperation {
  operationId: string;
}

export interface WorkbookOperationSnapshot {
  operation: AiSpreadsheetOperation | null;
  isPending: boolean;
  errorMessage: string | null;
  retry: (() => void) | null;
}

export interface WorkbookOperationRunResult {
  operation: AiSpreadsheetOperation;
  adoptedConflict: boolean;
}

const INITIAL_SNAPSHOT: WorkbookOperationSnapshot = {
  operation: null,
  isPending: false,
  errorMessage: null,
  retry: null,
};
const POLL_DELAYS_MS = [400, 700, 1_200, 2_000, 3_500, 5_000];
const CONFLICT_RESUBMIT_DELAYS_MS = [250, 500, 1_000, 2_000];
const MAX_POLL_DURATION_MS = 5 * 60 * 1_000;
const WORKBOOK_OPERATION_PENDING_ERROR = "workbook_operation_pending";
const CONFLICT_RETRY_MESSAGE =
  "Another Grid recalculation is still clearing. Retry this edit in a moment.";
const ACTIVE_OPERATION_STATUSES = new Set<AiSpreadsheetOperationStatus>([
  AI_SPREADSHEET_OPERATION_STATUS.queued,
  AI_SPREADSHEET_OPERATION_STATUS.running,
]);
const OPERATION_STATUSES = new Set<AiSpreadsheetOperationStatus>(
  Object.values(AI_SPREADSHEET_OPERATION_STATUS),
);
const snapshots = new Map<string, WorkbookOperationSnapshot>();
const listeners = new Map<string, Set<() => void>>();
const controllers = new Map<string, AbortController>();
const consumers = new Map<string, number>();

const scopeKey = (businessId: string, runId: string) =>
  `${businessId}:${runId}`;

const storageKey = (businessId: string, runId: string) =>
  `ai-spreadsheet-operation:${scopeKey(businessId, runId)}`;

const getSnapshot = (key: string) => snapshots.get(key) ?? INITIAL_SNAPSHOT;

export const readWorkbookOperationSnapshot = (
  businessId: string,
  runId: string,
) => getSnapshot(scopeKey(businessId, runId));

const emit = (key: string) =>
  listeners.get(key)?.forEach((listener) => listener());

const setSnapshot = (key: string, snapshot: WorkbookOperationSnapshot) => {
  snapshots.set(key, snapshot);
  emit(key);
};

const persistOperation = (
  businessId: string,
  runId: string,
  operation: AiSpreadsheetOperation,
) => {
  if (typeof localStorage === "undefined") return;
  const persisted: PersistedOperation = {
    operationId: operation.id,
  };
  try {
    localStorage.setItem(
      storageKey(businessId, runId),
      JSON.stringify(persisted),
    );
  } catch {
    // Browser storage is recovery-only and must never break a live mutation.
  }
};

const clearPersistedOperation = (businessId: string, runId: string) => {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(storageKey(businessId, runId));
  } catch {
    // Storage may be unavailable in restricted browser contexts.
  }
};

export const readPersistedWorkbookOperation = (
  businessId: string,
  runId: string,
): PersistedOperation | null => {
  if (typeof localStorage === "undefined") return null;

  try {
    const value = localStorage.getItem(storageKey(businessId, runId));
    if (!value) return null;

    const parsed = JSON.parse(value) as Partial<PersistedOperation>;
    if (typeof parsed.operationId === "string")
      return parsed as PersistedOperation;

    clearPersistedOperation(businessId, runId);
    return null;
  } catch {
    clearPersistedOperation(businessId, runId);
    return null;
  }
};

export const getWorkbookOperationPath = (
  businessId: string,
  runId: string,
  operationId: string,
) =>
  `/businesses/${businessId}/ai_script_runs/${runId}/spreadsheet/operations/${operationId}`;

const wait = (delay: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Workbook polling stopped", "AbortError"));
      return;
    }

    const onAbort = () => {
      globalThis.clearTimeout(timeout);
      reject(new DOMException("Workbook polling stopped", "AbortError"));
    };
    const timeout = globalThis.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, delay);
    signal.addEventListener("abort", onAbort, { once: true });
  });

const isOperationStatus = (
  value: unknown,
): value is AiSpreadsheetOperationStatus =>
  typeof value === "string" &&
  OPERATION_STATUSES.has(value as AiSpreadsheetOperationStatus);

const isOperation = (value: unknown): value is AiSpreadsheetOperation => {
  if (!value || typeof value !== "object") return false;
  const operation = value as Partial<AiSpreadsheetOperation>;
  return (
    typeof operation.id === "string" &&
    typeof operation.operation_type === "string" &&
    isOperationStatus(operation.status)
  );
};

const operationFromConflict = (error: unknown) => {
  if (!isAxiosError(error) || error.response?.status !== 409) return null;
  const data = error.response.data as {
    error?: string;
    operation?: unknown;
  };
  return data.error === WORKBOOK_OPERATION_PENDING_ERROR &&
    isOperation(data.operation)
    ? data.operation
    : null;
};

const pollOperation = async (
  api: AxiosInstance,
  businessId: string,
  runId: string,
  initialOperation: AiSpreadsheetOperation,
  signal: AbortSignal,
) => {
  const key = scopeKey(businessId, runId);
  let operation = initialOperation;
  let attempt = 0;
  let transientFailures = 0;
  const startedAt = dayjs();

  while (ACTIVE_OPERATION_STATUSES.has(operation.status)) {
    if (dayjs().diff(startedAt, "milliseconds") >= MAX_POLL_DURATION_MS) {
      throw new Error(
        "Grid recalculation is taking longer than expected. You can retry from this browser to keep checking it.",
      );
    }

    await wait(
      POLL_DELAYS_MS[Math.min(attempt, POLL_DELAYS_MS.length - 1)],
      signal,
    );
    attempt += 1;

    try {
      const response = await api.get<{ operation: AiSpreadsheetOperation }>(
        getWorkbookOperationPath(businessId, runId, operation.id),
        { signal },
      );
      if (!isOperation(response.data.operation)) {
        throw new Error("The Grid operation response was not recognized.");
      }
      operation = response.data.operation;
      transientFailures = 0;
      setSnapshot(key, {
        operation,
        isPending: true,
        errorMessage: null,
        retry: null,
      });
      persistOperation(businessId, runId, operation);
    } catch (error) {
      if (signal.aborted) throw error;
      transientFailures += 1;
      if (transientFailures >= 5) throw error;
    }
  }

  return operation;
};

export class WorkbookOperationFailedError extends Error {
  constructor(public operation: AiSpreadsheetOperation) {
    super(operation.error?.message ?? "Grid recalculation failed.");
    this.name = "WorkbookOperationFailedError";
  }
}

export class WorkbookOperationPollingError extends Error {
  constructor(
    public operation: AiSpreadsheetOperation,
    message: string,
  ) {
    super(message);
    this.name = "WorkbookOperationPollingError";
  }
}

export const runWorkbookOperation = async ({
  api,
  businessId,
  runId,
  submit,
}: {
  api: AxiosInstance;
  businessId: string;
  runId: string;
  submit: () => Promise<AcceptedAiSpreadsheetOperationResponse>;
}): Promise<WorkbookOperationRunResult> => {
  const key = scopeKey(businessId, runId);
  controllers.get(key)?.abort();
  const controller = new AbortController();
  controllers.set(key, controller);

  let operation: AiSpreadsheetOperation;
  let adoptedConflict = false;
  try {
    const accepted = await submit();
    if (!isOperation(accepted.operation)) {
      throw new Error("The Grid edit response was not recognized.");
    }
    operation = accepted.operation;
  } catch (error) {
    const conflictingOperation = operationFromConflict(error);
    if (!conflictingOperation) {
      if (controllers.get(key) === controller) controllers.delete(key);
      throw error;
    }
    operation = conflictingOperation;
    adoptedConflict = true;
  }

  setSnapshot(key, {
    operation,
    isPending: true,
    errorMessage: null,
    retry: null,
  });
  persistOperation(businessId, runId, operation);

  try {
    const terminal = await pollOperation(
      api,
      businessId,
      runId,
      operation,
      controller.signal,
    );
    if (terminal.status === AI_SPREADSHEET_OPERATION_STATUS.failed) {
      clearPersistedOperation(businessId, runId);
      if (adoptedConflict) {
        return { operation: terminal, adoptedConflict };
      }

      setSnapshot(key, {
        operation: terminal,
        isPending: false,
        errorMessage: terminal.error?.message ?? "Grid recalculation failed.",
        retry: null,
      });
      throw new WorkbookOperationFailedError(terminal);
    }
    return { operation: terminal, adoptedConflict };
  } catch (error) {
    if (
      error instanceof WorkbookOperationFailedError ||
      controller.signal.aborted
    ) {
      throw error;
    }

    const latestOperation = getSnapshot(key).operation ?? operation;
    const message =
      error instanceof Error
        ? error.message
        : "Grid recalculation status could not be refreshed.";
    setSnapshot(key, {
      operation: latestOperation,
      isPending: false,
      errorMessage: message,
      retry: null,
    });
    throw new WorkbookOperationPollingError(latestOperation, message);
  } finally {
    if (controllers.get(key) === controller) controllers.delete(key);
  }
};

/**
 * Waits out operations started elsewhere, then resubmits this mutation with the
 * same idempotency key. The backend deliberately allows only one active
 * recalculation per workbook, so a 409 is a sequencing signal rather than a
 * failed user edit.
 */
export const runWorkbookOperationThroughConflicts = async (args: {
  api: AxiosInstance;
  businessId: string;
  runId: string;
  submit: () => Promise<AcceptedAiSpreadsheetOperationResponse>;
  signal?: AbortSignal;
}) => {
  let result = await runWorkbookOperation(args);
  const signal = args.signal ?? new AbortController().signal;

  for (const delay of CONFLICT_RESUBMIT_DELAYS_MS) {
    if (!result.adoptedConflict) return result.operation;

    await wait(delay, signal);
    result = await runWorkbookOperation(args);
  }

  if (result.adoptedConflict) {
    clearPersistedOperation(args.businessId, args.runId);
    const key = scopeKey(args.businessId, args.runId);
    setSnapshot(key, {
      operation: result.operation,
      isPending: false,
      errorMessage: CONFLICT_RETRY_MESSAGE,
      retry: null,
    });
    throw new WorkbookOperationPollingError(
      result.operation,
      CONFLICT_RETRY_MESSAGE,
    );
  }

  return result.operation;
};

export const recoverWorkbookOperation = async (
  api: AxiosInstance,
  businessId: string,
  runId: string,
  signal?: AbortSignal,
) => {
  const persisted = readPersistedWorkbookOperation(businessId, runId);
  if (!persisted) return null;

  const response = await api.get<{ operation: AiSpreadsheetOperation }>(
    getWorkbookOperationPath(businessId, runId, persisted.operationId),
    { signal },
  );
  if (signal?.aborted) {
    throw new DOMException("Workbook recovery stopped", "AbortError");
  }
  if (!isOperation(response.data.operation)) {
    clearPersistedOperation(businessId, runId);
    throw new Error("The Grid operation response was not recognized.");
  }

  return runWorkbookOperation({
    api,
    businessId,
    runId,
    submit: async () => ({
      operation: response.data.operation,
      idempotent_replay: true,
    }),
  });
};

export const completeWorkbookOperation = (
  businessId: string,
  runId: string,
) => {
  clearPersistedOperation(businessId, runId);
  const key = scopeKey(businessId, runId);
  snapshots.delete(key);
  emit(key);
};

export const cancelWorkbookOperationPolling = (
  businessId: string,
  runId: string,
) => {
  const key = scopeKey(businessId, runId);
  controllers.get(key)?.abort();
  controllers.delete(key);
};

export const setWorkbookOperationRetry = (
  businessId: string,
  runId: string,
  retry: (() => void) | null,
) => {
  const key = scopeKey(businessId, runId);
  setSnapshot(key, { ...getSnapshot(key), retry });
};

export const setWorkbookOperationRecoveryError = (
  businessId: string,
  runId: string,
  errorMessage: string,
  retry: () => void,
) => {
  const key = scopeKey(businessId, runId);
  setSnapshot(key, {
    ...getSnapshot(key),
    isPending: false,
    errorMessage,
    retry,
  });
};

export const useWorkbookOperationSnapshot = (
  businessId: string | null,
  runId: string | null,
) => {
  const key = businessId && runId ? scopeKey(businessId, runId) : "";
  const snapshot = useSyncExternalStore(
    (listener) => {
      if (!key) return () => undefined;
      const scopedListeners = listeners.get(key) ?? new Set();
      scopedListeners.add(listener);
      listeners.set(key, scopedListeners);
      return () => {
        scopedListeners.delete(listener);
        if (scopedListeners.size === 0) listeners.delete(key);
      };
    },
    () => (key ? getSnapshot(key) : INITIAL_SNAPSHOT),
    () => INITIAL_SNAPSHOT,
  );

  useEffect(() => {
    if (!key) return;
    consumers.set(key, (consumers.get(key) ?? 0) + 1);
    return () => {
      const remaining = Math.max((consumers.get(key) ?? 1) - 1, 0);
      if (remaining === 0) {
        consumers.delete(key);
        if (businessId && runId) {
          cancelWorkbookOperationPolling(businessId, runId);
        }
      } else {
        consumers.set(key, remaining);
      }
    };
  }, [key]);

  return snapshot;
};
